/**
 * The one HTTP function every generated call goes through (orval `mutator`). The SPA configures
 * it once at boot (`configureApi`) with the API origin, the bearer token and the active org; it
 * then adds, per docs/08:
 *   - `Authorization: Bearer …` and `X-Org-Id` (the active org; omitted for /v1/me, /v1/admin);
 *   - `Idempotency-Key` on every mutation attempt (required on money/issuance routes, harmless
 *     elsewhere); callers may pass their own to retry the same attempt;
 *   - `X-Request-Id`, echoed by the API and used as the events' correlation id.
 * Errors become `ApiError` carrying the RFC 7807 problem (402 upsell payload included).
 */
export type Problem = {
  type?: string;
  title?: string;
  status: number;
  code?: string;
  detail?: string;
  requestId?: string;
  /** 402 ENTITLEMENT_MISSING */
  feature?: string;
  reason?: string;
  plan_hint?: { code: string; name: string } | null;
  upgrade_url?: string;
  /** 400 VALIDATION_FAILED */
  errors?: { path: (string | number)[]; message: string }[];
  /** 422 BUSINESS_RULE */
  rule?: string;
  [key: string]: unknown;
};

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title ?? `HTTP ${problem.status}`);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }
  get code(): string | undefined {
    return this.problem.code;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export type ApiConfig = {
  /** API origin, e.g. `http://localhost:3000` (empty = same origin). */
  baseUrl: string;
  getToken(): string | null;
  getOrgId(): string | null;
  /**
   * Called on the first 401 of a request (not an /v1/auth/* call): renew the access token from the
   * refresh token and resolve true to retry once with the new token. The retry reuses the original
   * Idempotency-Key / X-Request-Id, so a retried mutation is the same attempt.
   */
  refresh?(): Promise<boolean>;
  /** Called on 401 (expired token, no refresh available): the shell signs out. */
  onUnauthorized?(problem: Problem): void;
  fetch?: typeof fetch;
};

let config: ApiConfig = { baseUrl: '', getToken: () => null, getOrgId: () => null };

export function configureApi(next: Partial<ApiConfig>): void {
  config = { ...config, ...next };
}

export function apiConfig(): Readonly<ApiConfig> {
  return config;
}

/** Routes that never take X-Org-Id (user-level and platform routes). */
function isOrgless(path: string): boolean {
  return /^\/v1\/(me|admin|auth|public|lobby|onboarding|invitations)(\/|$|\?)/.test(path);
}

/** Auth routes must never trigger a refresh-and-retry (that is what would be refreshing). */
function isAuthPath(path: string): boolean {
  return /^\/v1\/auth(\/|$|\?)/.test(path);
}

function uuid(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
  );
}

export type OexRequestInit = RequestInit & {
  /** Override the active org for this call (org-management routes carry it in the path anyway). */
  orgId?: string | null;
};

export async function oexFetch<T>(url: string, init: OexRequestInit = {}): Promise<T> {
  const { orgId: orgOverride, ...options } = init;
  const method = (options.method ?? 'GET').toUpperCase();
  // Stable headers computed once so a refresh-and-retry is the SAME attempt (idempotency-key).
  const base = new Headers(options.headers);
  const callerAuth = base.has('authorization');
  const orgId = orgOverride === undefined ? config.getOrgId() : orgOverride;
  if (orgId && !isOrgless(url) && !base.has('x-org-id')) base.set('x-org-id', orgId);
  if (method !== 'GET' && method !== 'HEAD' && !base.has('idempotency-key'))
    base.set('idempotency-key', uuid());
  if (!base.has('x-request-id')) base.set('x-request-id', uuid());
  if (options.body !== undefined && !base.has('content-type'))
    base.set('content-type', 'application/json');
  const doFetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  const attempt = () => {
    const headers = new Headers(base);
    const token = config.getToken();
    if (token && !callerAuth) headers.set('authorization', `Bearer ${token}`);
    return doFetch(`${config.baseUrl}${url}`, { ...options, method, headers });
  };
  let res = await attempt();
  if (res.status === 401 && config.refresh && !isAuthPath(url) && (await config.refresh())) {
    res = await attempt();
  }
  const type = res.headers.get('content-type') ?? '';
  const text = res.status === 204 ? '' : await res.text();
  const body: unknown = type.includes('json') && text ? JSON.parse(text) : text;
  if (!res.ok) {
    const problem: Problem =
      body && typeof body === 'object'
        ? ({ status: res.status, ...(body as object) } as Problem)
        : { status: res.status, title: res.statusText, detail: typeof body === 'string' ? body : undefined };
    if (res.status === 401) config.onUnauthorized?.(problem);
    throw new ApiError(problem);
  }
  return body as T;
}

export default oexFetch;
